import React, { useEffect, useState } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, CircularProgress,
    TablePagination, MenuItem, Alert, Snackbar
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
    getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, getDocs,
    query, orderBy
} from "firebase/firestore";
import Payroleheader from './Payroleheader';

const db = getFirestore();

function Staffadvance() {
    const [data, setData] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        staffid: "",
        advanceamount: "",
        reason: "",
        date: new Date().toISOString().split("T")[0],
        status: "No",
    });
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Snackbar for alerts
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    // Summary values
    const [totalAdvance, setTotalAdvance] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [totalDeducted, setTotalDeducted] = useState(0);

    // Fetch staff list
    useEffect(() => {
        const fetchStaff = async () => {
            const snap = await getDocs(collection(db, "staffdetails"));
            setStaffList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        };
        fetchStaff();
    }, []);

    // Fetch main table data
    const fetchData = async () => {
        setLoading(true);
        try {
            const staffAdvanceCollection = collection(db, "staffadvance");
            const q = query(staffAdvanceCollection, orderBy("date", "desc"));
            const snap = await getDocs(q);
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

            setData(list);

        } catch (err) {
            console.error("Error fetching data:", err);
            setAlert({ open: true, message: "Error fetching data.", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // SUMMARY CALCULATION (works on filtered search also)
    const calculateSummary = (list) => {
        const total = list.reduce((sum, r) => sum + Number(r.advanceamount || 0), 0);

        const deducted = list
            .filter((r) => r.status === "Yes")
            .reduce((sum, r) => sum + Number(r.advanceamount || 0), 0);

        const pending = total - deducted;

        setTotalAdvance(total);
        setTotalDeducted(deducted);
        setTotalPending(pending);
    };


    // Search Filter + Summary update on every search
    const filteredData = React.useMemo(() => {
        const result = data.filter((row) => {
            const staff = staffList.find((s) => s.id === row.staffid);
            return staff?.staffname?.toLowerCase().includes(search.toLowerCase());
        });

        // Recalculate summary based on filtered results
        calculateSummary(result);

        return result;
    }, [search, data, staffList]);


    // Pagination
    const handleChangePage = (e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // Dialog open / close
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setFormData({
            staffid: "",
            advanceamount: "",
            reason: "",
            date: new Date().toISOString().split("T")[0],
            status: "No",
        });
        setEditId(null);
        setOpen(false);
    };

    // Save / Update
    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (editId) {
                await updateDoc(doc(db, "staffadvance", editId), {
                    advanceamount: formData.advanceamount,
                    reason: formData.reason,
                    date: formData.date,
                    status: formData.status
                });
                setAlert({ open: true, message: "Advance updated successfully!", severity: "success" });
            } else {
                await addDoc(collection(db, "staffadvance"), formData);
                setAlert({ open: true, message: "Advance added successfully!", severity: "success" });
            }
            fetchData();
            handleClose();
        } catch (err) {
            console.error("Error saving data:", err);
            setAlert({ open: true, message: "Error saving data.", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditId(row.id);
        setOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, "staffadvance", id));
            fetchData();
            setAlert({ open: true, message: "Advance deleted successfully!", severity: "success" });
        } catch (err) {
            console.error("Error deleting document: ", err);
            setAlert({ open: true, message: "Error deleting advance.", severity: "error" });
        }
    };

    return (
        <Box p={3}>
            <Payroleheader />

            <Box display="flex" justifyContent="space-between" mb={2}>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpen}>
                    Add उचल पेमेंट
                </Button>
                <Typography variant="h5" fontWeight="bold">स्टाफ उचल पेमेंट</Typography>
            </Box>

            {/* SEARCH */}
            <TextField
                label="Search Staff"
                size="small"
                fullWidth
                margin="normal"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            {/* SUMMARY CARDS */}
            <Box display="flex" gap={2} mt={2} mb={2}>
                <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2">Total Advance</Typography>
                    <Typography variant="h6" color="primary">{totalAdvance} ₹</Typography>
                </Paper>

                <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2">Deducted</Typography>
                    <Typography variant="h6" color="green">{totalDeducted} ₹</Typography>
                </Paper>

                <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2">Pending</Typography>
                    <Typography variant="h6" color="red">{totalPending} ₹</Typography>
                </Paper>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>स्टाफ नाव</TableCell>
                                <TableCell>उचल रक्कम</TableCell>
                                <TableCell>कारण</TableCell>
                                <TableCell>तारीख</TableCell>
                                <TableCell>उचल वजावट</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => {
                                    const staff = staffList.find((s) => s.id === row.staffid);
                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell>{staff?.staffname || "Unknown"}</TableCell>
                                            <TableCell>{row.advanceamount}</TableCell>
                                            <TableCell>{row.reason}</TableCell>
                                            <TableCell>{row.date}</TableCell>
                                            <TableCell style={{ color: row.status === "Yes" ? "green" : "red" }}>
                                                {row.status}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEdit(row)}
                                                // disabled={row.status === "Yes"}
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDelete(row.id)}
                                                    disabled={row.status === "Yes"}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* PAGINATION */}
            <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* DIALOG */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editId ? "उचल पेमेंट अपडेट करा" : "उचल पेमेंट जोडा"}</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        margin="normal"
                        label="स्टाफ नाव"
                        value={formData.staffid}
                        onChange={(e) => setFormData({ ...formData, staffid: e.target.value })}
                        disabled={!!editId}
                    >
                        {staffList.map((s) => (
                            <MenuItem key={s.id} value={s.id}>{s.staffname}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        margin="normal"
                        label="उचल रक्कम"
                        value={formData.advanceamount}
                        onChange={(e) => setFormData({ ...formData, advanceamount: e.target.value })}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        margin="normal"
                        label="कारण"
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        margin="normal"
                        label="उचल तारीख"
                        InputLabelProps={{ shrink: true }}
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />

                    {editId && (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            margin="normal"
                            label="स्टेटस"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                        </TextField>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="error">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : editId ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ALERT */}
            <Snackbar open={alert.open} autoHideDuration={6000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity} sx={{ width: "100%" }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Staffadvance;
